import React from "react";
import AuthorizedPage from '../../component/AuthorizedPage';
import { Table, Form, Row, Col, ButtonGroup, Button } from "react-bootstrap";
import ProductItemApi from '../../api/ProductItemApi';
import WarehouseApi from "../../api/WarehouseApi";

type StockHistoryItem = {
  id: number;
  productItemId: number;
  quantityChange: number;
  quantityAfterChange: number;
  transactionDate: string;
  warehouseId: number;
  warehouseTitle?: string;
  actionLabel?: string;
  notes?: string; // note alanını ekledik
  createdBy?: string; // işlemi gerçekleştiren kişi
};

type ProductItem = {
  id: number;
  name: string;
  stockCode?: string;
};

type Warehouse = {
  id: number;
  name?: string;
  title?: string;
};

type State = {
  stockHistoryItems: StockHistoryItem[];
  warehouseIdFilter: number | null;
  productIdFilter: number | null;
  actionFilter: string | null;
  startDateFilter: string;
  endDateFilter: string;
  buyerWarehouses: Warehouse[];
  productItems: ProductItem[];
};

class StockHistory extends React.Component<any, State> {
  state: State = {
    stockHistoryItems: [],
    warehouseIdFilter: null,
    productIdFilter: null,
    actionFilter: null,
    startDateFilter: "",
    endDateFilter: "",
    buyerWarehouses: [],
    productItems: [],
  };

async componentDidMount() {
  const today = new Date().toISOString().split("T")[0];
  this.setState({ startDateFilter: today, endDateFilter: today }, async () => {
    await this.loadWarehouses();
    await this.loadProductItems();
    await this.loadStockHistory();
  });
}

  async loadWarehouses() {
    const response = await WarehouseApi.listAllWarehouses();
    if (response.success) {
      this.setState({ buyerWarehouses: response.data });
    }
  }

  async loadProductItems() {
    const response = await ProductItemApi.listAllByPackagingGroup("");
    if (response.success) {
      this.setState({ productItems: response.data });
    }
  }

  async loadStockHistory() {
    const { warehouseIdFilter, productIdFilter, startDateFilter, endDateFilter, buyerWarehouses } = this.state;

    const params: any = {};
    if (warehouseIdFilter) params.warehouseId = warehouseIdFilter;
    if (productIdFilter) params.productItemId = productIdFilter;
    if (this.state.actionFilter) params.action = this.state.actionFilter;
    if (startDateFilter) params.startDate = `${startDateFilter}T00:00:00`;
    if (endDateFilter) params.endDate = `${endDateFilter}T23:59:59`;

    const response = await ProductItemApi.listStockHistory(params);
    if (response.success) {
      const warehousesMap = new Map<number, string>();
      buyerWarehouses.forEach((w: Warehouse) =>
        warehousesMap.set(w.id, w.name || w.title || "Depo")
      );

      const dataWithNames: StockHistoryItem[] = response.data.map(
        (item: any, index: number) => ({
          id: index,
          productItemId: item.productItemId,
          quantityChange: item.quantityChange,
          quantityAfterChange: item.quantityAfterChange,
          transactionDate: item.createdAt,
          warehouseId: item.warehouseId,
          warehouseTitle: warehousesMap.get(item.warehouseId) || "-",
          actionLabel: item.action?.label || item.action?.name || "-",
          notes: item.notes || "-", // note alanını ekledik
          createdBy: item.createdBy || "-", // işlemi gerçekleştiren kişi
        })
      );

      this.setState({ stockHistoryItems: dataWithNames });
    }
  }

  handleFilterChange = (field: string, value: any) => {
    this.setState({ [field]: value } as any, () => {
      this.loadStockHistory();
    });
  };

  handleQuickFilter = (period: "1w" | "1m" | "3m" | "1y") => {
    const now = new Date();
    let start = new Date();

    switch (period) {
      case "1w":
        start.setDate(now.getDate() - 7);
        break;
      case "1m":
        start.setMonth(now.getMonth() - 1);
        break;
      case "3m":
        start.setMonth(now.getMonth() - 3);
        break;
      case "1y":
        start.setFullYear(now.getFullYear() - 1);
        break;
    }

    const startDate = start.toISOString().split("T")[0];
    const endDate = now.toISOString().split("T")[0];

    this.setState(
      { startDateFilter: startDate, endDateFilter: endDate },
      () => this.loadStockHistory()
    );
  };

  getProductName(productItemId: number) {
    const product = this.state.productItems.find((p: ProductItem) => p.id === productItemId);
    return product ? product.name : `#${productItemId}`;
  }

  render() {
    return (
      <AuthorizedPage>
        <h2>Stok Geçmişi</h2>

        <Form className="mb-3">
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Depo Filtresi</Form.Label>
                <Form.Select
                  value={this.state.warehouseIdFilter || ""}
                  onChange={e =>
                    this.handleFilterChange(
                      "warehouseIdFilter",
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                >
                  <option value="">Tüm Depolar</option>
                  {this.state.buyerWarehouses.map((w: Warehouse) => (
                    <option key={w.id} value={w.id}>
                      {w.name || w.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Ürün Filtresi</Form.Label>
                <Form.Select
                  value={this.state.productIdFilter || ""}
                  onChange={e =>
                    this.handleFilterChange(
                      "productIdFilter",
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                >
                  <option value="">Tüm Ürünler</option>
                  {this.state.productItems.map((p: ProductItem) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>İşlem Türü</Form.Label>
                <Form.Select
                  value={this.state.actionFilter || ""}
                  onChange={e =>
                    this.handleFilterChange(
                      "actionFilter",
                      e.target.value || null
                    )
                  }
                >
                  <option value="">Tümü</option>
                  {/* IMPORT_OPTIONS */}
                  <option value="PRODUCTION_INBOUND">Üretimden Mal Girişi</option>
                  <option value="RETURN_INBOUND">İade Girişi</option>
                  <option value="CONSIGNMENT_INBOUND">Emanet Girişi</option>
                  <option value="TRANSFER_INBOUND">Depo Transfer Girişi</option>
                  <option value="PURCHASE_INBOUND">Satın Alma Girişi</option>

                  {/* EXPORT_OPTIONS */}
                  <option value="SHIPMENT_OUTBOUND">Sevkiyat Çıkışı</option>
                  <option value="BRANCH_OUTBOUND">Şube Çıkışı</option>
                  <option value="SALES_REP_OUTBOUND">Plasiyer Çıkışı</option>
                  <option value="INDIVIDUAL_OUTBOUND">Bireysel Çıkış</option>
                  <option value="REWORK_OUTBOUND">Rework Çıkışı</option>
                  <option value="PACKAGING_OUTBOUND">Paketleme Çıkışı</option>
                  <option value="FACTORY_SALES_POINT_OUTBOUND">Fabrika Satış Noktası Çıkışı</option>
                  <option value="TRANSFER_OUTBOUND">Depo Transfer Çıkışı</option>
                </Form.Select>
              </Form.Group>

            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Başlangıç Tarihi</Form.Label>
                <Form.Control
                  type="date"
                  value={this.state.startDateFilter}
                  onChange={e =>
                    this.handleFilterChange("startDateFilter", e.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Bitiş Tarihi</Form.Label>
                <Form.Control
                  type="date"
                  value={this.state.endDateFilter}
                  onChange={e =>
                    this.handleFilterChange("endDateFilter", e.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Label>Hızlı Filtreler</Form.Label>
              <ButtonGroup className="d-flex">
                <Button size="sm" variant="outline-primary" onClick={() => this.handleQuickFilter("1w")}>1H</Button>
                <Button size="sm" variant="outline-primary" onClick={() => this.handleQuickFilter("1m")}>1A</Button>
                <Button size="sm" variant="outline-primary" onClick={() => this.handleQuickFilter("3m")}>3A</Button>
                <Button size="sm" variant="outline-primary" onClick={() => this.handleQuickFilter("1y")}>1Y</Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Form>

        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Ürün Adı</th>
              <th>Depo</th>
              <th>İşlem Türü</th>
              <th>Açıklama</th> {/* notes alanı */}
              <th>Miktar Değişimi</th>
              <th>Son Stok Miktarı</th>
              <th>Tarih</th>
              <th>İşlemi Gerçekleştiren</th>
            </tr>
          </thead>
          <tbody>
            {this.state.stockHistoryItems.map((item: StockHistoryItem) => (
              <tr key={item.id}>
                <td>{this.getProductName(item.productItemId)}</td>
                <td>{item.warehouseTitle || "-"}</td>
                <td>{item.actionLabel}</td>
                <td>{item.notes || "-"}</td> {/* notes gösterimi */}
                <td>{item.quantityChange}</td>
                <td>{item.quantityAfterChange}</td>
                <td>{new Date(item.transactionDate).toLocaleString()}</td>
                <td>{item.createdBy || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </AuthorizedPage>
    );
  }
}

export default StockHistory;
